import React, { useMemo } from "react";
import { usePost } from "../../../../../../api/useCall";
import { useCategories, useCreateCategory } from "../../../../../../api/useCategories";
import {
  useCreateProjectCategory,
  useProjectCategories,
} from "../../../../../../api/useProjectCategories";
import { useQuantities } from "../../../../../../api/useQuantities";
import type { Setup } from "../../../../../../api/useSetups";
import { useCanUserDo, useCanUserDoOnProject } from "../../../../../canUserDo";
import t from "../../../../../../i18n";
import type { RowValue } from "../../../../../../lib/csv";
import { getMissingFailuresMandatoryColumns } from "../../../../../../lib/csv";
import useToasts from "@ui/Toast/useToasts";
import type { ParsingError, ParsingResult } from "../../../../../../domain/parser/types";
import type { Result } from "../../../../../../lib/patterns/result";
import { Err, isResultErr, Ok } from "../../../../../../lib/patterns/result";
import { uniq } from "diagnostics-lib/array";
import { indexBy, indexByKey } from "diagnostics-lib/iterable";
import { normalize } from "diagnostics-lib/string";
import AdditionnalMessage from "@ui/AdditionnalMessage";
import DropzoneV2 from "@ui/DropzoneV2";
import ModalForm from "@ui/Modal/Form";
import { numberValidation } from "../../../../formValidationFunctions";
import type { MapFile } from "../../../validatorCSV";
import { createAndRefreshItems, createCSVValidator } from "../../../validatorCSV";
import { checkAndParseContent, findUnkownColumns } from "./checkAndParseContent";
import type { Categories, FailuresFile, FileKeys, FixedColumn } from "./FilesType";
import {
  expectedColumns,
  FAILURE_PARSING_ERROR,
  getFailureParsingErrorMessage,
  getMandatoryColumnsMap,
} from "./FilesType";
import { ImportNewCategoryInfo } from "./ImportNewCategoryInfo";
import { options, validatorFilterFormulaFunction } from "./optionsValidation";
import { FILE_PARSING_ERROR } from "../../../../../../domain/parser/common/file";
import useActiveAnalysesStream from "../../../../../../api/useActiveAnalysesStream";
import type { Project } from "../../../../../../api/useProjects";
import type { Reparability } from "../../../../../../api/Reparability";
import { getAnalysisSetup } from "diagnostics-domain/setup/getSetup";
import { presetsWithoutPromotion } from "diagnostics-domain/setup/presets";
interface ImportFailuresImportFormProps {
  onRequestClose: () => void;
  project: Project;
  setup: Setup;
  setups: Setup[];
  onSetEditedDataKey: (key?: string) => void;
}

export const pushFileToMap = (
  key: FileKeys,
  mapObject: Map<FileKeys, MapFile<FixedColumn, Categories>>,
  fileName: string,
) =>
  mapObject.get(key)?.fileName.push(fileName) ??
  mapObject.set(key, {
    fileName: [fileName],
    toCreate: [],
    error: { format: [], permissionRequired: [] },
  });

function getUnknownQuantitiesHeaders(
  content: string,
  quantityNames: string[],
  mandatoryColumns: readonly string[],
) {
  const namesImpacts = findUnkownColumns(content, mandatoryColumns);
  return namesImpacts.filter(f => !quantityNames.find(mandatory => f === mandatory));
}

export function getMissingMandatoryColumns(content: string) {
  const required = getMandatoryColumnsMap();
  return getMissingFailuresMandatoryColumns(content, required);
}

function getInvalidImpacts(content: RowValue[], quantityNames: string[]) {
  const impacts: { line: RowValue; failureName: string; quantitieName: string }[] = [];

  for (const line of content) {
    const { name } = line;
    for (const value in line) {
      if (quantityNames.includes(value)) {
        const impact = line[value] ?? "";
        if (!impact || (impact && numberValidation(impact))) {
          impacts.push({ line, failureName: name ?? "unknown", quantitieName: value });
        }
      }
    }
  }

  return impacts;
}

export const hasInvalidPermissionCategoryValidation = (
  category: string,
  categoriesDB: string[],
) => {
  const presentCategory = categoriesDB.find(c => c === normalize(category));
  return presentCategory === undefined;
};

const validateFilterType = (rows: RowValue[], diagnosisPreset: string) => {
  const allowedValues = presetsWithoutPromotion.has(diagnosisPreset)
    ? new Set(["CONSTRAINT"])
    : new Set(["CONSTRAINT", "PROMOTION"]);

  const invalidFailures = new Set<string>();

  for (const row of rows) {
    for (const key of Object.keys(row)) {
      if (key.startsWith("filterType")) {
        const value = row[key]?.trim();
        if (value && !allowedValues.has(value)) {
          invalidFailures.add(row["name"] ?? "unknown");
        }
      }
    }
  }

  return {
    invalidFailures: Array.from(invalidFailures),
    allowedValues: Array.from(allowedValues),
  };
};

/**
 * @param files list of file to check
 * @param quantityNames quantity of the project
 * @returns validation Map<FileKeys, MapFile>
 */
export async function validateFailures(
  file: File,
  quantityNames: string[],
  nameSystems: string[],
  namesEquipmentTypes: string[],
  namesDegradationTypes: string[],
  namesFailureCategories: string[],
  namesEquipments: string[],
  namesSuspectedElements: string[],
  canEdit: {
    system: boolean;
    equipmentType: boolean;
    degradationType: boolean;
    failureCategory: boolean;
    equipments: boolean;
    suspectedElements: boolean;
  },
  diagnosisPreset: string,
): Promise<Result<ParsingResult<FailuresFile>, ParsingError<FAILURE_PARSING_ERROR>[]>> {
  // bad header in csv failure file
  const errors: ParsingError<FAILURE_PARSING_ERROR>[] = [];
  const fileContent = await file.text();
  const { fileName, content } = await checkAndParseContent(file);

  if (!content)
    errors.push({
      kind: FAILURE_PARSING_ERROR.MISSING_ALL_COLUMNS,
      explain: "file missing all columns",
      location: { file: file.name, line: 0 },
      data: {},
    });

  const missingMandatoryColumns = getMissingMandatoryColumns(fileContent);

  if (missingMandatoryColumns.length > 0)
    errors.push({
      kind: FAILURE_PARSING_ERROR.MISSING_COLUMNS,
      explain: "file missing mandatory columns",
      location: { file: file.name, line: 0 },
      data: {
        value: missingMandatoryColumns,
      },
    });

  const unknownFailures = getUnknownQuantitiesHeaders(fileContent, quantityNames, expectedColumns);
  if (unknownFailures.length > 0)
    errors.push({
      kind: FAILURE_PARSING_ERROR.UNKNOWN_QUANTITY,
      explain: "file unknown quantity",
      location: { file: file.name, line: 0 },
      data: {
        value: unknownFailures,
      },
    });

  const invalidImpacts = getInvalidImpacts(content, quantityNames);
  if (invalidImpacts?.length > 0) {
    errors.push({
      kind: FAILURE_PARSING_ERROR.IMPACT_VALUE_NOT_A_NUMBER,
      explain: "file invalid impact value",
      location: { file: file.name, line: 0 },
      data: {
        value: invalidImpacts.map(({ failureName, quantitieName }) => [failureName, quantitieName]),
      },
    });
  }

  const checkFilterFormulaQuantity = validatorFilterFormulaFunction(
    content,
    /^MEAN\((.+)\)$/,
    match => {
      const quantityName = match?.[1];
      return quantityName && !quantityNames.includes(quantityName) ? true : false;
    },
  );
  if (checkFilterFormulaQuantity.length > 0) {
    errors.push({
      kind: FAILURE_PARSING_ERROR.FILTER_FORMULA_UNKNOWN_QUANTITY,
      explain: "file invalid filter formula",
      location: { file: file.name, line: 0 },
      data: {
        value: checkFilterFormulaQuantity.map(({ name }) => name),
      },
    });
  }

  const { invalidFailures: invalidFilterTypes, allowedValues } = validateFilterType(
    content,
    diagnosisPreset,
  );
  if (invalidFilterTypes.length > 0) {
    errors.push({
      kind: FAILURE_PARSING_ERROR.INVALID_FILTER_TYPE,
      explain: "file invalid filterType value",
      location: { file: file.name, line: 0 },
      data: {
        value: { failures: invalidFilterTypes, allowed: allowedValues },
      },
    });
  }

  const validation = new Map<FileKeys, MapFile<FixedColumn, Categories>>([
    [
      "failures",
      {
        fileName: [file.name],
        toCreate: [],
        error: { format: [], permissionRequired: [] },
      },
    ],
  ]);

  createCSVValidator(validation.get("failures")!, content, options, {
    canEdit,
    normalizedPresentData: {
      degradationType: namesDegradationTypes,
      equipmentType: namesEquipmentTypes,
      failureCategory: namesFailureCategories,
      system: nameSystems,
      equipments: namesEquipments,
      suspectedElements: namesSuspectedElements,
    },
  });

  const failureErrors = validation.get("failures")?.error?.format;
  const permissionErrors = validation.get("failures")?.error?.permissionRequired;

  if (failureErrors && failureErrors.length > 0) {
    for (const e of failureErrors) {
      if (!e) continue;
      errors.push({
        kind: e.message as FAILURE_PARSING_ERROR,
        explain: "validation error",
        location: { file: file.name, line: 0 },
        data: {
          value: {
            data: e.data,
            rowNames: e.rowNames,
            columnNames: e.columnNames,
            lines: e.lines,
          },
        },
      });
    }
  }

  if (permissionErrors && permissionErrors.length > 0) {
    // if (!permissionErrors[0]?.message) throw new Error("permissionError must have a message");
    for (const e of permissionErrors) {
      errors.push({
        kind: e.message as FAILURE_PARSING_ERROR,
        explain: "permission required",
        location: { file: file.name, line: 0 },
        data: {
          value: {
            data: e.data,
            column: e.column,
            rowNames: e.rowNames,
            columnNames: e.columnNames,
            lines: e.lines,
          },
        },
      });
    }
  }

  return errors.length > 0
    ? Err(errors)
    : Ok({
        data: {
          file,
          fileName,
          content,
          toCreate: validation.get("failures")?.toCreate ?? [],
        },
      });
}

export const ImportFailuresModalForm = ({
  project,
  setups,
  setup,
  onRequestClose,
  onSetEditedDataKey,
}: ImportFailuresImportFormProps) => {
  const canCategoryEdit = useCanUserDo("CATEGORY_EDIT");
  const canCategoryProjectEdit = useCanUserDoOnProject("PROJECT_CATEGORY_EDIT", project.id);
  const canEdit = useMemo(
    () => ({
      degradationType: canCategoryEdit,
      equipmentType: canCategoryEdit,
      failureCategory: canCategoryEdit,
      system: canCategoryEdit,
      equipments: canCategoryProjectEdit,
      suspectedElements: canCategoryProjectEdit,
    }),
    [canCategoryEdit, canCategoryProjectEdit],
  );

  const [valid, setValid] = React.useState<boolean>();
  const [formError, setFormError] = React.useState<
    ParsingError<FAILURE_PARSING_ERROR | FILE_PARSING_ERROR>[]
  >([]);
  const importEndpoint = usePost(
    "/projects/:projectId(uuid)/setups/:setupId(uuid)/diagnosis/failures",
  );
  const [fileName, setFileName] = React.useState<string>("");
  const quantities = useQuantities(project.id);
  const { activeAnalyses } = useActiveAnalysesStream(project.id);
  const indexQuantitiesByName = indexByKey(quantities, "name");
  const quantityNames = quantities.map((quantity: { name: string }) => quantity.name);
  const { addToast } = useToasts();
  const systems = useCategories("SYSTEM");
  const equipmentTypes = useCategories("EQUIPMENT_TYPE");
  const degradationTypes = useCategories("DEGRADATION_TYPE");
  const failureCategories = useCategories("FAILURE_CATEGORY");
  const equipments = useProjectCategories(project.id, "EQUIPMENT");
  const suspectedElements = useProjectCategories(project.id, "SUSPECTED_ELEMENT");

  const createSystem = useCreateCategory("SYSTEM");
  const createEquipmentType = useCreateCategory("EQUIPMENT_TYPE");
  const createDegradationType = useCreateCategory("DEGRADATION_TYPE");
  const createFailureCategory = useCreateCategory("FAILURE_CATEGORY");
  const createEquipments = useCreateProjectCategory(project.id, "EQUIPMENT");
  const createSuspectedElements = useCreateProjectCategory(project.id, "SUSPECTED_ELEMENT");

  const indexNameSystem = useMemo(() => new Set(systems), [systems]);
  const indexNameEquipmentType = useMemo(() => new Set(equipmentTypes), [equipmentTypes]);
  const indexNameDegradationType = useMemo(() => new Set(degradationTypes), [degradationTypes]);
  const indexNameFailureCategory = useMemo(() => new Set(failureCategories), [failureCategories]);
  const indexNameEquipments = useMemo(() => indexBy(equipments, "name"), [equipments]);
  const indexNameSuspectedElements = useMemo(
    () => indexBy(suspectedElements, "name"),
    [suspectedElements],
  );

  const normalizedNamesSystems = useMemo(() => systems.map(m => normalize(m)), [systems]);
  const normalizedNamesEquipmentTypes = useMemo(
    () => equipmentTypes.map(m => normalize(m)),
    [equipmentTypes],
  );
  const normalizedNameDegradationType = useMemo(
    () => degradationTypes.map(m => normalize(m)),
    [degradationTypes],
  );
  const normalizedNameFailureCategory = useMemo(
    () => failureCategories.map(m => normalize(m)),
    [failureCategories],
  );
  const normalizedNamesEquipments = useMemo(
    () => equipments.map(m => normalize(m.name)),
    [equipments],
  );
  const normalizedNamesSuspectedElements = useMemo(
    () => suspectedElements.map(m => normalize(m.name)),
    [suspectedElements],
  );

  const [uploadedFiles, setUploadedFiles] = React.useState<ParsingResult<FailuresFile> | null>(
    null,
  );

  const impactedAnalysisCount = React.useMemo(
    () =>
      activeAnalyses.filter(
        a =>
          (a.status === "DIAGNOSIS" || a.status === "DIAGNOSIS_ERROR") &&
          getAnalysisSetup(setups, a)?.id === setup.id,
      ).length,
    [activeAnalyses, setups, setup.id],
  );

  const downloadHandler = React.useCallback(() => {
    const error = JSON.stringify(formError, null, 4);
    const blob = new Blob([error], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", `${new Date().toISOString()}_failure_import_errors.json`);
    link.click();
    link.remove();
  }, [formError]);

  const handleDrop = React.useCallback(
    async (files: File[]) => {
      setFormError([]);
      setValid(false);
      setUploadedFiles(null);

      if (files.length === 0 || !files[0]) return;

      setFileName(files[0].name);

      if (files[0].size === 0) {
        setValid(false);
        return setFormError([
          {
            kind: FILE_PARSING_ERROR.FILE_EMPTY,
            explain: "file empty",
            location: { file: files[0].name, line: 0 },
            data: {},
          },
        ]);
      }

      const importedFileObject = await validateFailures(
        files[0],
        quantityNames,
        normalizedNamesSystems,
        normalizedNamesEquipmentTypes,
        normalizedNameDegradationType,
        normalizedNameFailureCategory,
        normalizedNamesEquipments,
        normalizedNamesSuspectedElements,
        canEdit,
        setup.diagnosisPreset,
      );

      if (isResultErr(importedFileObject)) {
        setValid(false);
        return setFormError(importedFileObject.value);
      }

      setUploadedFiles(importedFileObject.value);
      setValid(true);
    },
    [
      quantityNames,
      normalizedNamesSystems,
      normalizedNamesEquipmentTypes,
      normalizedNameDegradationType,
      normalizedNameFailureCategory,
      normalizedNamesEquipments,
      normalizedNamesSuspectedElements,
      canEdit,
      setup.diagnosisPreset,
    ],
  );

  function getValueOrDefault<T>(value: T | undefined, indexNew: Set<T>, index: Set<T>): T | null {
    return value && (indexNew.has(value) || index.has(value)) ? value : null;
  }

  const handleSubmit = React.useCallback(async () => {
    const permissionsRequired = uploadedFiles?.data.toCreate;
    const newSystems: string[] = [];
    const newEquipmentType: string[] = [];
    const newDegradationType: string[] = [];
    const newFailureCategory: string[] = [];
    const newEquipments: { id: string; name: string }[] = [];
    const newSuspectedElements: { id: string; name: string }[] = [];
    if (permissionsRequired && permissionsRequired.length > 0) {
      const columnsToCreate: Record<string, string[]> = {};
      for (const error of permissionsRequired) {
        const values = error.values || [];
        if (values.length > 0) {
          const column = error.column;
          if (!columnsToCreate[column]) {
            columnsToCreate[column] = [];
          }
          columnsToCreate[column]?.push(...values);
        }
      }
      const {
        system: systemsToCreate = [],
        equipmentType: equipmentTypeToCreate = [],
        degradationType: degradationToCreate = [],
        failureCategory: failureCategoryToCreate = [],
        equipments: equipmentsToCreate = [],
        suspectedElements: suspectedElementsToCreate = [],
      } = columnsToCreate;

      try {
        await createAndRefreshItems(systemsToCreate, createSystem, newSystems);
        await createAndRefreshItems(equipmentTypeToCreate, createEquipmentType, newEquipmentType);
        await createAndRefreshItems(degradationToCreate, createDegradationType, newDegradationType);
        await createAndRefreshItems(
          failureCategoryToCreate,
          createFailureCategory,
          newFailureCategory,
        );
        await createAndRefreshItems(equipmentsToCreate, createEquipments, newEquipments);
        await createAndRefreshItems(
          suspectedElementsToCreate,
          createSuspectedElements,
          newSuspectedElements,
        );
      } catch (_) {
        setValid(false);
        return false;
      }
    }

    const indexNewNameSystem = new Set(newSystems);
    const indexNewNameEquipmentType = new Set(newEquipmentType);
    const indexNewNameDegradationType = new Set(newDegradationType);
    const indexNewNameFailureCategory = new Set(newFailureCategory);
    const indexNewNameEquipments = indexBy(newEquipments, "name");
    const indexNewNameSuspectedElements = indexBy(newSuspectedElements, "name");

    const parsedCsvArray = uploadedFiles?.data.content;
    const contentText = await uploadedFiles?.data.file.text();

    if (parsedCsvArray === undefined || contentText === undefined)
      throw new Error("Should not happens since this error has already been checked before");

    const failures = parsedCsvArray.map(fault => {
      const impactsName = fault["impacts"]?.split(",") as string[];
      const impacts = impactsName.map(nameColumn => ({
        quantityId: quantities.find(q => q.name === nameColumn)?.id ?? "",
        value: Number(fault[nameColumn]),
      }));
      const filters = Object.keys(fault).reduce(
        (acc, columnName) => {
          const matchColumnName = columnName.match(/^filterFormula(\d+)$/);
          if (matchColumnName) {
            const nth = matchColumnName[1];
            const filterFormulaValue = fault[columnName];
            const matchValue = filterFormulaValue?.match(/^(.*?)\((.*?)\)$/);
            if (matchValue) {
              const type = matchValue[1];
              if (type === "MEAN") {
                const quantityId = matchValue[2]
                  ? (indexQuantitiesByName.get(matchValue[2])?.id ?? null)
                  : null;
                const minValue = fault[`filterMin${nth}`];
                const min = `filterMin${nth}` in fault && minValue ? parseFloat(minValue) : null;
                const maxValue = fault[`filterMax${nth}`];
                const max = `filterMin${nth}` in fault && maxValue ? parseFloat(maxValue) : null;
                const filterType = `filterType${nth}` in fault ? fault[`filterType${nth}`] : null;
                if (quantityId && type && (min !== null || max !== null)) {
                  acc.push({
                    quantityId,
                    type,
                    min,
                    max,
                    rule:
                      filterType === null
                        ? "CONSTRAINT"
                        : (filterType as "CONSTRAINT" | "PROMOTION"),
                  });
                }
              } else {
                throw new Error(`Incorrect validation of diagnostic file : ${type}`);
              }
            }
          }
          return acc;
        },
        [] as {
          quantityId: string;
          type: "MEAN";
          min: number | null;
          max: number | null;
          rule: "CONSTRAINT" | "PROMOTION";
        }[],
      );

      const {
        system: nameSystem,
        equipmentType: nameEquipmentType,
        degradationType: nameDegradationType,
        failureCategory: nameFailureCategory,
        equipments: nameEquipments,
        suspectedElements: nameSuspectedElements,
        reparability,
      } = fault;

      const technicalName = (fault["name"] ?? fault["technicalName"]) as string;
      const displayName = (fault["displayName"] as string) ?? technicalName;

      const failure = {
        name: technicalName,
        displayName,
        unit: fault["unit"] as string,
        description: fault["description"] as string,
        enabled: Boolean(Number(fault["activated"])),
        min: Number(fault["minimumValue"]),
        max: Number(fault["maximumValue"]),
        amplitude: fault["magnitude"] ? Number(fault["magnitude"]) : 0,
        systemId: getValueOrDefault(nameSystem, indexNewNameSystem, indexNameSystem),
        equipmentTypeId: getValueOrDefault(
          nameEquipmentType,
          indexNewNameEquipmentType,
          indexNameEquipmentType,
        ),
        degradationTypeId: getValueOrDefault(
          nameDegradationType,
          indexNewNameDegradationType,
          indexNameDegradationType,
        ),
        failureCategoryId: getValueOrDefault(
          nameFailureCategory,
          indexNewNameFailureCategory,
          indexNameFailureCategory,
        ),
        reparability: reparability ? (reparability as Reparability) : null,
        equipmentIds:
          uniq(nameEquipments?.split(",") ?? []).flatMap(
            m => (indexNewNameEquipments.get(m)?.id || indexNameEquipments.get(m)?.id) ?? [],
            [],
          ) ?? [],
        suspectedElementIds:
          uniq(nameSuspectedElements?.split(",") ?? []).flatMap(
            m =>
              (indexNewNameSuspectedElements.get(m)?.id || indexNameSuspectedElements.get(m)?.id) ??
              [],
            [],
          ) ?? [],
        impacts,
        filters,
      };
      return failure;
    });

    const { status } = await importEndpoint({
      routeParams: { projectId: project.id, setupId: setup.id },
      body: { fileName, inputs: failures },
    });
    if (status === 204) {
      onSetEditedDataKey();
      addToast("success", t("setup.diagnosis.failures.import_checking"));
    }
    return true;
  }, [
    uploadedFiles?.data.toCreate,
    uploadedFiles?.data.content,
    uploadedFiles?.data.file,
    importEndpoint,
    project.id,
    setup.id,
    createSystem,
    createEquipmentType,
    createDegradationType,
    createFailureCategory,
    createEquipments,
    createSuspectedElements,
    indexNameSystem,
    indexNameEquipmentType,
    indexNameDegradationType,
    indexNameFailureCategory,
    quantities,
    indexQuantitiesByName,
    indexNameEquipments,
    indexNameSuspectedElements,
    onSetEditedDataKey,
    addToast,
    fileName,
  ]);

  return (
    <ModalForm
      onRequestClose={onRequestClose}
      onSubmit={uploadedFiles && valid ? handleSubmit : false}
      okButtonName={t("modalForm.button.name.import")}
    >
      <DropzoneV2
        onDrop={handleDrop}
        accept=".csv"
        nameFile={
          valid
            ? uploadedFiles?.data.fileName
            : formError.length > 0
              ? formError[0]?.location.file
              : ""
        }
        validation={valid}
        errorText={!valid && formError[0] ? getFailureParsingErrorMessage(formError[0]) : undefined}
        downloadErrors={downloadHandler}
        pathSampleFile="/metroscopia_example_failures.csv"
      />
      {uploadedFiles && valid && <ImportNewCategoryInfo toCreate={uploadedFiles.data.toCreate} />}
      {uploadedFiles && impactedAnalysisCount > 0 && (
        <AdditionnalMessage
          status="WARNING"
          text={t("setup.paragraphs.model.modal.delete_model.warningText", {
            impactedAnalysisCount,
          })}
        />
      )}
    </ModalForm>
  );
};
